import React, { useMemo, forwardRef, useContext, useState, useRef, useEffect, cloneElement, useLayoutEffect, useCallback } from 'react';

const LayerContext = React.createContext({
    ownLayer: { current: null },
    isDisabled: false,
    setDisabled: () => { },
    hasRoot: false,
});

const noop = () => { };
/**
 * Set React.ref value whether it's a function or object ref
 */
function setReactRef(ref, value) {
    if (typeof ref === "function") {
        ref(value);
    }
    else if (ref && typeof ref === "object") {
        // eslint-disable-next-line no-param-reassign
        ref.current = value;
    }
}
/**
 * Adapted from material-ui/blob/master/packages/material-ui/src/utils/useForkRef.js
 */
function useForkRef(ref1, ref2) {
    return useMemo(() => {
        if (ref1 == null && ref2 == null) {
            return noop;
        }
        return (refValue) => {
            if (ref1) {
                setReactRef(ref1, refValue);
            }
            if (ref2) {
                setReactRef(ref2, refValue);
            }
        };
    }, [ref1, ref2]);
}

/**
 * Establish a new layer for click away listeners.
 *
 * This requires the root app component to be wrapped in `<Layer root>`.
 *
 * `<ClickAwayListener>` elements outside of the topmost layer will be disabled until the
 * layer is unmounted.
 */
const ClickAwayLayer = forwardRef(({ children, root = false }, parentRef) => {
    const parentLayer = useContext(LayerContext);
    const [isDisabled, setDisabled] = useState(false);
    const setParentDisabled = parentLayer?.setDisabled;
    const ownRef = useRef(null);
    const partialRef = useForkRef(ownRef, parentRef);
    const combinedRef = useForkRef(
    // @ts-expect-error
    children.ref, partialRef);
    // Disable the parent layer on mount
    useEffect(() => {
        if (root) {
            return;
        }
        setParentDisabled?.(true);
        return () => setParentDisabled?.(false);
    }, [root, setParentDisabled, ownRef]);
    const updatedContext = {
        ...parentLayer,
        ownLayer: ownRef,
        hasRoot: root || parentLayer.hasRoot,
        isDisabled,
        setDisabled,
    };
    return (React.createElement(LayerContext.Provider, { value: updatedContext }, root || !children
        ? children
        : cloneElement(children, { ref: combinedRef })));
});

const warn = (warning) => () => {
    throw new Error(warning);
};
/**
 * Adapted from https://reactjs.org/docs/hooks-faq.html#how-to-read-an-
 * often-changing-value-from-usecallback.
 *
 * See also https://github.com/facebook/react/issues/16154 and `useEventCallback` in
 * https://github.com/formium/formik/blob/31405ab/packages/formik/src/Formik.tsx
 * and https://mobile.twitter.com/diegohaz/status/1324168516061245441
 */
function useRefCallback(callback, dependencies) {
    const ref = useRef(
    // @ts-expect-error
    warn("Cannot call an event handler while rendering."));
    useLayoutEffect(() => {
        ref.current = callback;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callback, ...dependencies]);
    const frozenCallback = useCallback((...args) => {
        const fn = ref.current;
        // @ts-ignore
        return fn(...args);
    }, [ref]);
    return frozenCallback;
}

// Name of a child prop to label debug messages.
const debugLabelProp = "label";
const compact = (array) => array.filter((x) => !!x);
const log = (label, ...args) => {
};
function clickedRootScrollbar(event, doc) {
    return (doc.documentElement.clientWidth < event.clientX ||
        doc.documentElement.clientHeight < event.clientY);
}
const mouseEventMap = {
    click: "onClickCapture",
    mouseup: "onMouseUpCapture",
    mousedown: "onMouseDownCapture",
};
const touchEventMap = {
    touchstart: "onTouchStartCapture",
    touchend: "onTouchEndCapture",
};
const eventPhaseLabel = {
    0: "no phase",
    1: "capture phase",
    2: "at target",
    3: "bubble phase",
};
const getEventLabel = (event) => `${event.type} event (${eventPhaseLabel[event.eventPhase]})`;
/**
 * Listen for click events that occur somewhere in the document, outside of the element
 * itself. For instance, if you need to hide a menu when people click anywhere else on
 * your page.
 */
const ClickAwayListener = ({ children: child, disableReactTree = false, mouseEvent = "mousedown", onClickAway, touchEvent = "touchstart", ignoreScrollbars = false, layer: layerProp, }) => {
    const movedRef = useRef(false);
    const nodeRef = useRef(null);
    const activatedRef = useRef(false);
    const didHandleSyntheticEvent = useRef(false);
    const shouldAttemptClickaway = useRef(false);
    const lastNativeEvent = useRef();
    const mouseEventHandler = mouseEvent ? mouseEventMap[mouseEvent] : mouseEvent;
    const touchEventHandler = touchEvent ? touchEventMap[touchEvent] : touchEvent;
    const label = child?.props?.[debugLabelProp]; // for debugging
    const ownerDocument = nodeRef.current?.ownerDocument ?? document;
    const context = useContext(LayerContext);
    const { isDisabled } = context;
    const layerElement = layerProp ?? context.ownLayer.current ?? ownerDocument.documentElement;
    useEffect(() => {
        // Ensure that this component is not "activated" synchronously.
        // https://github.com/facebook/react/issues/20074
        setTimeout(() => {
            activatedRef.current = true;
        }, 0);
        return () => {
            activatedRef.current = false;
        };
    }, []);
    const handleRef = useForkRef(
    // @ts-expect-error
    child.ref, nodeRef);
    const attemptClickAway = useRefCallback(() => {
        const event = lastNativeEvent.current;
        const insideReactTree = didHandleSyntheticEvent.current;
        const shouldAttempt = shouldAttemptClickaway.current;
        didHandleSyntheticEvent.current = false;
        shouldAttemptClickaway.current = false;
        if (isDisabled || !shouldAttempt) {
            return;
        }
        const didClickScrollbar = !ignoreScrollbars &&
            "clientX" in event &&
            clickedRootScrollbar(event, ownerDocument);
        // 1. IE11 support, which trigger the handleClickAway even after the unbind
        // 2. The child might render null.
        // 3. Behave like a blur listener.
        if (!activatedRef.current || !nodeRef.current || didClickScrollbar) {
            return;
        }
        // Do not act if user performed touchmove
        if (movedRef.current) {
            movedRef.current = false;
            return;
        }
        const insideDOM = !layerElement.contains(
        // @ts-expect-error
        event.target) ||
            nodeRef.current.contains(
            // @ts-expect-error
            event.target);
        const isClickAway = !insideDOM && (disableReactTree || !insideReactTree);
        if (isClickAway) ;
        else {
            const reasons = compact([
                insideDOM && "a DOM tree descendant",
                !disableReactTree && insideReactTree && "a React tree descendant",
            ]);
            `the event target is ${reasons.join(" and ")}`;
        }
        if (isClickAway) {
            onClickAway(event);
        }
    }, [onClickAway, disableReactTree, ownerDocument, layerElement, isDisabled]);
    const handleNativeEvent = useRefCallback((event) => {
        log(label, `handling ${getEventLabel(event)} from document`, {
            event,
            isDisabled,
            nodeRef,
        });
        if (isDisabled) {
            return;
        }
        shouldAttemptClickaway.current = true;
        lastNativeEvent.current = event;
        // This capture phase click away handler will be called before the synthetic event
        // handler. We need to wait for the synthetic event to check whether the event comes
        // from inside the React tree.
        setTimeout(() => {
            attemptClickAway();
        });
    }, [isDisabled, attemptClickAway]);
    // Keep track of mouse/touch events that bubbled up through the portal.
    const createHandleSynthetic = (handlerName) => (event) => {
        log(label, `handling ${getEventLabel(event)} from React descendant`, {
            isDisabled,
            event,
        });
        if (isDisabled) {
            return;
        }
        didHandleSyntheticEvent.current = true;
        const childrenPropsHandler = child?.props?.[handlerName];
        childrenPropsHandler?.(event);
        attemptClickAway();
    };
    const childrenProps = { ref: handleRef };
    if (touchEventHandler !== false) {
        childrenProps[touchEventHandler] = createHandleSynthetic(touchEventHandler);
    }
    useEffect(() => {
        if (touchEvent !== false) {
            const handleTouchMove = () => {
                movedRef.current = true;
            };
            const handleCapturePhase = handleNativeEvent;
            const handleBubblePhase = attemptClickAway;
            document.addEventListener(touchEvent, handleCapturePhase, true);
            document.addEventListener("touchmove", handleTouchMove, true);
            document.addEventListener(touchEvent, handleBubblePhase);
            return () => {
                document.removeEventListener(touchEvent, handleCapturePhase, true);
                document.removeEventListener("touchmove", handleTouchMove, true);
                document.addEventListener(touchEvent, handleBubblePhase);
            };
        }
    }, [handleNativeEvent, attemptClickAway, touchEvent]);
    if (mouseEventHandler !== false) {
        childrenProps[mouseEventHandler] = createHandleSynthetic(mouseEventHandler);
    }
    useEffect(() => {
        if (mouseEvent) {
            const handleCapturePhase = handleNativeEvent;
            const handleBubblePhase = attemptClickAway;
            document.addEventListener(mouseEvent, handleCapturePhase, true);
            document.addEventListener(mouseEvent, handleBubblePhase);
            return () => {
                document.removeEventListener(mouseEvent, handleCapturePhase, true);
                document.addEventListener(mouseEvent, handleBubblePhase);
            };
        }
    }, [handleNativeEvent, attemptClickAway, mouseEvent]);
    return cloneElement(child, childrenProps);
};

const eventNames = {
    mouse: [
        "onAuxClick",
        "onClick",
        "onContextMenu",
        "onDoubleClick",
        "onMouseDown",
        "onMouseEnter",
        "onMouseLeave",
        "onMouseMove",
        "onMouseOut",
        "onMouseOver",
        "onMouseUp",
    ],
    touch: ["onTouchCancel", "onTouchEnd", "onTouchMove", "onTouchStart"],
    drag: [
        "onDrag",
        "onDragEnd",
        "onDragEnter",
        "onDragExit",
        "onDragLeave",
        "onDragOver",
        "onDragStart",
        "onDrop",
    ],
    keyboard: ["onKeyDown", "onKeyPress", "onKeyUp"],
};

/**
 * Combine two event handlers (e.g. onClick callbacks)
 */
const useCombinedHandler = (callback1, callback2) => useCallback((event) => {
    callback1?.(event);
    callback2?.(event);
}, [callback1, callback2]);

const fromPairs = (arr) => arr.reduce((acc, val) => ((acc[val[0]] = val[1]), acc), {});
/**
 * Stops event bubbling to the parent element.
 *
 * Capture phase will stay unaffected.
 */
const StopPropagation = forwardRef((props, parentRef) => {
    const { children, all, mouse, touch, drag, keyboard, excludeNativeEvents, ...rest } = props;
    const hasSpecifier = [all, mouse, touch, drag, keyboard].some((x) => typeof x === "boolean");
    if (!hasSpecifier) {
        throw new Error("At least one <StopPropagation> prop must be a boolean.");
    }
    const combinedRef = useForkRef(
    // @ts-expect-error
    children?.ref, parentRef);
    const handleEvent = useCallback((event) => {
        event.stopPropagation();
        if (!excludeNativeEvents) {
            event.nativeEvent?.stopImmediatePropagation?.();
        }
    }, [excludeNativeEvents]);
    const handlers = fromPairs(Object.keys(eventNames).map((kind) => {
        const group = eventNames[kind].map((eventName) => {
            // This hook will be always invoked the same amount of times and in the same
            // order.
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const combinedHandler = useCombinedHandler(children?.props[eventName], handleEvent);
            return [eventName, combinedHandler];
        });
        return [kind, fromPairs(group)];
    }));
    if (!children) {
        return null;
    }
    return cloneElement(children, {
        ...rest,
        ref: combinedRef,
        ...(all || mouse ? handlers.mouse : null),
        ...(all || touch ? handlers.touch : null),
        ...(all || drag ? handlers.drag : null),
        ...(all || keyboard ? handlers.keyboard : null),
    });
});

export { ClickAwayLayer, ClickAwayListener, ClickAwayLayer as Layer, StopPropagation, ClickAwayListener as default };
//# sourceMappingURL=index.js.map
